import { Vector } from "../Core/Utils/Vector";
var _random = Math.random;
export function setRandom() {
  var rnd = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Math.random;
  _random = rnd;
}
export function getRandom() {
  return clamp(_random(), 0, 1 - 1e-16);
}
export function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}
export function mix(comp1, comp2, weight1, weight2) {
  return Math.floor((comp1 * weight1 + comp2 * weight2) / (weight1 + weight2));
}
export function randomInRange(r) {
  var max = getRangeMax(r);
  var min = getRangeMin(r);

  if (max === min) {
    min = 0;
  }

  return getRandom() * (max - min) + min;
}
export function getRangeValue(value) {
  return typeof value === "number" ? value : randomInRange(value);
}
export function getRangeMin(value) {
  return typeof value === "number" ? value : value.min;
}
export function getRangeMax(value) {
  return typeof value === "number" ? value : value.max;
}
export function setRangeValue(source, value) {
  if (source === value || value === undefined && typeof source === "number") {
    return source;
  }

  var min = getRangeMin(source),
      max = getRangeMax(source);
  return value !== undefined ? {
    min: Math.min(min, value),
    max: Math.max(max, value)
  } : setRangeValue(min, max);
}
export function getValue(options) {
  var random = options.random,
      _ref = typeof random === "boolean" ? {
    enable: random,
    minimumValue: 0
  } : random,
      enable = _ref.enable,
      minimumValue = _ref.minimumValue;

  return enable ? getRangeValue(setRangeValue(options.value, minimumValue)) : getRangeValue(options.value);
}
export function getDistances(pointA, pointB) {
  var dx = pointA.x - pointB.x,
      dy = pointA.y - pointB.y;
  return {
    dx: dx,
    dy: dy,
    distance: Math.sqrt(dx * dx + dy * dy)
  };
}
export function getDistance(pointA, pointB) {
  return getDistances(pointA, pointB).distance;
}
export function getParticleDirectionAngle(direction, position, center) {
  if (typeof direction === "number") {
    return direction * Math.PI / 180;
  } else {
    switch (direction) {
      case "top":
        return -Math.PI / 2;

      case "top-right":
        return -Math.PI / 4;

      case "right":
        return 0;

      case "bottom-right":
        return Math.PI / 4;

      case "bottom":
        return Math.PI / 2;

      case "bottom-left":
        return 3 * Math.PI / 4;

      case "left":
        return Math.PI;

      case "top-left":
        return -3 * Math.PI / 4;

      case "inside":
        return Math.atan2(center.y - position.y, center.x - position.x);

      case "outside":
        return Math.atan2(position.y - center.y, position.x - center.x);

      case "none":
      default:
        return getRandom() * Math.PI * 2;
    }
  }
}
export function getParticleBaseVelocity(direction) {
  var baseVelocity = Vector.origin;
  baseVelocity.length = 1;
  baseVelocity.angle = direction;
  return baseVelocity;
}
export function collisionVelocity(v1, v2, m1, m2) {
  return Vector.create(v1.x * (m1 - m2) / (m1 + m2) + v2.x * 2 * m2 / (m1 + m2), v1.y);
}
export function calcEasing(value, type) {
  switch (type) {
    case "ease-in-quad":
      return Math.pow(value, 2);

    case "ease-out-quad":
      return 1 - Math.pow(1 - value, 2);

    case "ease-in-out-quad":
      return value < 0.5 ? 2 * Math.pow(value, 2) : 1 - Math.pow(-2 * value + 2, 2) / 2;

    case "ease-in-cubic":
      return Math.pow(value, 3);

    case "ease-out-cubic":
      return 1 - Math.pow(1 - value, 3);

    case "ease-in-out-cubic":
      return value < 0.5 ? 4 * Math.pow(value, 3) : 1 - Math.pow(-2 * value + 2, 3) / 2;

    case "ease-in-quart":
      return Math.pow(value, 4);

    case "ease-out-quart":
      return 1 - Math.pow(1 - value, 4);

    case "ease-in-out-quart":
      return value < 0.5 ? 8 * Math.pow(value, 4) : 1 - Math.pow(-2 * value + 2, 4) / 2;

    case "ease-in-quint":
      return Math.pow(value, 5);

    case "ease-out-quint":
      return 1 - Math.pow(1 - value, 5);

    case "ease-in-out-quint":
      return value < 0.5 ? 16 * Math.pow(value, 5) : 1 - Math.pow(-2 * value + 2, 5) / 2;

    case "ease-in-expo":
      return !value ? 0 : Math.pow(2, 10 * value - 10);

    case "ease-out-expo":
      return value === 1 ? 1 : 1 - Math.pow(2, -10 * value);

    case "ease-in-out-expo":
      return !value ? 0 : value === 1 ? 1 : value < 0.5 ? Math.pow(2, 20 * value - 10) / 2 : (2 - Math.pow(2, -20 * value + 10)) / 2;

    case "ease-in-sine":
      return 1 - Math.cos(value * Math.PI / 2);

    case "ease-out-sine":
      return Math.sin(value * Math.PI / 2);

    case "ease-in-out-sine":
      return -(Math.cos(Math.PI * value) - 1) / 2;

    case "ease-in-back":
      {
        var c1 = 1.70158,
            c3 = c1 + 1;
        return c3 * Math.pow(value, 3) - c1 * Math.pow(value, 2);
      }

    case "ease-out-back":
      {
        var _c2 = 1.70158,
            _c3 = _c2 + 1;

        return 1 + _c3 * Math.pow(value - 1, 3) + _c2 * Math.pow(value - 1, 2);
      }

    case "ease-in-out-back":
      {
        var _c4 = 1.70158,
            c2 = _c4 * 1.525;
        return value < 0.5 ? Math.pow(2 * value, 2) * ((c2 + 1) * 2 * value - c2) / 2 : (Math.pow(2 * value - 2, 2) * ((c2 + 1) * (value * 2 - 2) + c2) + 2) / 2;
      }

    case "ease-in-circ":
      return 1 - Math.sqrt(1 - Math.pow(value, 2));

    case "ease-out-circ":
      return Math.sqrt(1 - Math.pow(value - 1, 2));

    case "ease-in-out-circ":
      return value < 0.5 ? (1 - Math.sqrt(1 - Math.pow(2 * value, 2))) / 2 : (Math.sqrt(1 - Math.pow(-2 * value + 2, 2)) + 1) / 2;

    default:
      return value;
  }
}
export function calcPositionFromSize(data) {
  var _a, _b;

  return ((_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== undefined && ((_b = data.position) === null || _b === void 0 ? void 0 : _b.y) !== undefined ? {
    x: data.position.x * data.size.width / 100,
    y: data.position.y * data.size.height / 100
  } : undefined;
}
export function calcPositionOrRandomFromSize(data) {
  var _a, _b, _c, _d;

  return {
    x: ((_b = (_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : getRandom() * 100) * data.size.width / 100,
    y: ((_d = (_c = data.position) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : getRandom() * 100) * data.size.height / 100
  };
}
export function calcPositionOrRandomFromSizeRanged(data) {
  var _a, _b;

  var position = {
    x: ((_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== undefined ? getRangeValue(data.position.x) : undefined,
    y: ((_b = data.position) === null || _b === void 0 ? void 0 : _b.y) !== undefined ? getRangeValue(data.position.y) : undefined
  };
  return calcPositionOrRandomFromSize({
    size: data.size,
    position: position
  });
}
export function calcExactPositionOrRandomFromSize(data) {
  var _a, _b, _c, _d;

  return {
    x: (_b = (_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : getRandom() * data.size.width,
    y: (_d = (_c = data.position) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : getRandom() * data.size.height
  };
}
export function calcExactPositionOrRandomFromSizeRanged(data) {
  var _a, _b;

  var position = {
    x: ((_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== undefined ? getRangeValue(data.position.x) : undefined,
    y: ((_b = data.position) === null || _b === void 0 ? void 0 : _b.y) !== undefined ? getRangeValue(data.position.y) : undefined
  };
  return calcExactPositionOrRandomFromSize({
    size: data.size,
    position: position
  });
}
export function parseAlpha(input) {
  return input.endsWith("%") ? parseFloat(input) / 100 : parseFloat(input);
}